/**
 * Translating between track time and buffer time.
 *
 * When a deck holds only a region of a track, the buffer's zero is not the
 * track's zero. Beats, peaks, cue points and the planner all speak track time;
 * the player speaks buffer time. Getting that translation wrong is exactly the
 * class of bug that has already cost this project twice — a clock time compared
 * against track-time beats, and a planner authorising a rate the deck could not
 * reach — so it lives here as a pure function with tests rather than as
 * arithmetic scattered through the deck.
 */
export interface LoadedWindow {
  /** Track-time second corresponding to buffer position 0. */
  readonly offset: number;
  /** Length of the loaded buffer, in seconds. */
  readonly length: number;
}

export const WHOLE_TRACK: LoadedWindow = { offset: 0, length: Infinity };

export function toBufferTime(window: LoadedWindow, trackTime: number): number {
  return trackTime - window.offset;
}

export function toTrackTime(window: LoadedWindow, bufferTime: number): number {
  return bufferTime + window.offset;
}

export function windowEnd(window: LoadedWindow): number {
  return window.offset + window.length;
}

/** Whether a track-time position is inside what this deck actually holds. */
export function contains(window: LoadedWindow, trackTime: number): boolean {
  return trackTime >= window.offset && trackTime <= windowEnd(window);
}

/** Clamp a track-time position to what is loaded, so a seek cannot fall off. */
export function clampToWindow(window: LoadedWindow, trackTime: number): number {
  return Math.max(window.offset, Math.min(trackTime, windowEnd(window)));
}

/** Bytes a decoded buffer of this length occupies, for the memory warning. */
export function decodedBytes(seconds: number, sampleRate = 48000, channels = 2): number {
  return seconds * sampleRate * channels * 4;
}
